import { wrapRequest } from '@applandeo/api-wrapper';
import { useCallback, useEffect, useState } from 'react';

import {
    getOwnAllLimitsUsageRequestApiV3,
    getOwnLimitsUsageRequest,
    getUserAllLimitsUsageRequestApiV4,
    getUserLimitsUsage,
} from '@/dataExchange/services/newRequest.service';
import type { Limit } from '@/domain/newRequest';
import { convertLimitsToLimitsUsageApiV2 } from '@/hooks/useUsedLimits/convertLimitsToLimitsUsage';
import { normalizeDateForAPI } from '@/utils/date/date';
import { GET_LIMITS_USAGE_RANGE_TYPE } from '@/utils/enums/newRequest';

export type LimitsUsage = Limit & {
    daysLeft: number;
};

type UsedLimitsProps = {
    userId?: string;
    isByPeriod?: boolean;
    includeArchived?: boolean;
};

/**
 * There are two similar enpoints to get used Limits but they are slightly different.
 * Please check and make sure it will cover your use case
 * @see {@link getOwnLimitsUsageRequest}
 * @see {@link getOwnAllLimitsUsageRequestApiV3}
 */
const useUsedLimits = ({ userId, isByPeriod, includeArchived }: UsedLimitsProps) => {
    const getLimitsUsageWrapper = wrapRequest(getOwnLimitsUsageRequest);
    const getAllLimitsUsageWrapper = wrapRequest(getOwnAllLimitsUsageRequestApiV3);
    const getUserAllLimitsUsageWrapper = wrapRequest(getUserAllLimitsUsageRequestApiV4);
    const getUserLimitsUsageWrapper = wrapRequest(getUserLimitsUsage);

    const [isLoading, setIsLoading] = useState(true);
    const [limitsUsageByPeriod, setLimitsUsageByPeriod] = useState<Limit[]>();
    const [allLimitsUsage, setAllLimitsUsage] = useState<LimitsUsage[]>();
    const [selectedRangeType, setSelectedRangeType] = useState(GET_LIMITS_USAGE_RANGE_TYPE.CURRENT_YEAR);

    const getOwnLimits = async (rangeType: GET_LIMITS_USAGE_RANGE_TYPE) => {
        const result = await getLimitsUsageWrapper.fetch({ rangeType, includeArchived }).concludeWithPromise();

        return result?.[0]?.buckets;
    };
    const getOwnAllLimits = async (date: string) => {
        const result = await getAllLimitsUsageWrapper.fetch({ date, includeArchived }).concludeWithPromise();

        return result;
    };
    const getUserAllLimits = async (userId: string, date: string) => {
        const result = await getUserAllLimitsUsageWrapper
            .fetch({ userId, date, includeArchived })
            .concludeWithPromise();

        return result;
    };

    const getUserLimits = async (userId: string, rangeType: GET_LIMITS_USAGE_RANGE_TYPE) => {
        if (!userId) return;

        const result = await getUserLimitsUsageWrapper
            .fetch({ userId, rangeType, includeArchived })
            .concludeWithPromise();

        return result?.[0]?.buckets;
    };

    const getLimitsByPeriod = async () => {
        const limitsUsage = userId
            ? await getUserLimits(userId, selectedRangeType)
            : await getOwnLimits(selectedRangeType);
        setIsLoading(false);
        if (!limitsUsage) return;
        const limitsUsageFiltered = limitsUsage.filter(limit => Boolean(limit) && limit.daysUsed > 0);
        setLimitsUsageByPeriod(limitsUsageFiltered);
    };

    const getAllLimits = async () => {
        const date = normalizeDateForAPI(new Date());
        // Both cases go through the same conversion, so an admin reading an employee gets the days the
        // API already corrected instead of a weaker calculation of our own.
        const allLimits = userId ? await getUserAllLimits(userId, date) : await getOwnAllLimits(date);
        const allLimitsUsage = allLimits && convertLimitsToLimitsUsageApiV2(allLimits);

        setIsLoading(false);
        setAllLimitsUsage(allLimitsUsage);
    };

    useEffect(() => {
        isByPeriod ? getLimitsByPeriod() : getAllLimits();
    }, [userId, isByPeriod, selectedRangeType]);

    const onSelectChange = useCallback(event => {
        setSelectedRangeType(event.target.value);
    }, []);

    return { limitsUsageByPeriod, allLimitsUsage, isLoading, onSelectChange, selectedRangeType, getAllLimits };
};

export default useUsedLimits;
